import { z } from "zod";
import { NPC_ARCHETYPES } from "@frontier/content/npcs";
import {
  CONTRACT_IDS,
  PASSIVE_IDS,
  SECTOR_IDS,
  SELECTABLE_CONTRACT_IDS,
} from "@frontier/content/contracts";
import {
  CONTRACT_CONTENT_VERSION,
  LEGACY_CONTRACT_CONTENT_VERSION,
  REWARDED_CONTRACT_CONTENT_VERSION,
  TERRITORIAL_CONTRACT_CONTENT_VERSION,
  CONTRACT_TEMPLATE_IDS,
  templateContractId,
  type ContractOffer,
  type ContractTerms,
} from "@frontier/content/contract-orders";
import {
  contractRewardTermsSchema,
  contractAccessTermsSchema,
} from "@frontier/content/reward-rules";
import { SURVEY_SITE_IDS } from "@frontier/content/progression";
import { contractNpcArchetypeSchema } from "./contract-npc-schema.js";
import { escortTermsSchema } from "./escort-schemas.js";
import { escortPhysicalKey } from "@frontier/content/escort-contracts";
export { contractNpcArchetypeSchema } from "./contract-npc-schema.js";

export const orderIdSchema = z.string().min(1).max(200);
const missionObjectiveBase = z
  .object({
    name: z.string(),
    x: z.number().finite(),
    y: z.number().finite(),
    kind: z.enum([
      "travel",
      "combat",
      "collect",
      "survey",
      "return",
      "complete",
    ]),
  })
  .strict();
export const missionObjectiveSchema = z.union([
  missionObjectiveBase.extend({ targetId: z.string() }),
  missionObjectiveBase,
]);

const contractTermsBase = z
  .object({
    templateId: z.enum(CONTRACT_TEMPLATE_IDS),
    id: z.enum(CONTRACT_IDS),
    title: z.string().min(1),
    description: z.string(),
    sectorId: z.enum(SECTOR_IDS),
    goal: z.number().int().positive(),
    cargoKind: z.enum(["energy", "salvage"]).nullable(),
    credits: z.string().regex(/^(0|[1-9]\d*)$/),
    components: z.number().int().nonnegative(),
    unlock: z.enum(PASSIVE_IDS),
    objective: missionObjectiveSchema,
    requiredArchetypes: z
      .array(contractNpcArchetypeSchema)
      .max(NPC_ARCHETYPES.length)
      .refine((items) => new Set(items).size === items.length),
    requiredSurveySites: z
      .array(z.enum(SURVEY_SITE_IDS))
      .max(3)
      .refine((items) => new Set(items).size === items.length),
    requireRescue: z.boolean(),
    requireElite: z.boolean(),
    cargoGoal: z.number().int().nonnegative(),
    escort: escortTermsSchema.nullable().default(null),
  })
  .strict();
export const contractTermsSchema = z
  .union([
    z.strictObject({
      contentVersion: z.literal(LEGACY_CONTRACT_CONTENT_VERSION),
      ...contractTermsBase.shape,
    }),
    z.strictObject({
      contentVersion: z.literal(REWARDED_CONTRACT_CONTENT_VERSION),
      ...contractTermsBase.shape,
      rewards: contractRewardTermsSchema,
      access: contractAccessTermsSchema.nullable(),
    }),
    z.strictObject({
      contentVersion: z.literal(TERRITORIAL_CONTRACT_CONTENT_VERSION),
      ...contractTermsBase.shape,
      cargoOriginSectorId: z.enum(SECTOR_IDS).exactOptional(),
      rewards: contractRewardTermsSchema,
      access: contractAccessTermsSchema.nullable(),
    }),
    z.strictObject({
      contentVersion: z.literal(CONTRACT_CONTENT_VERSION),
      ...contractTermsBase.shape,
      cargoOriginSectorId: z.enum(SECTOR_IDS).exactOptional(),
      killPolicy: z.enum(["any-hostile", "required-archetypes"]),
      rewards: contractRewardTermsSchema,
      access: contractAccessTermsSchema.nullable(),
    }),
  ])
  .refine((terms) => !("cargoOriginSectorId" in terms) ||
    (terms.cargoKind !== null && terms.cargoOriginSectorId === terms.sectorId), {
    message: "Collection origin must match the order's cargo region",
  })
  .refine((terms) => templateContractId(terms.templateId) === terms.id, {
    message: "Order template must belong to its contract family",
  })
  .refine((terms) => terms.contentVersion !== CONTRACT_CONTENT_VERSION ||
    terms.killPolicy !== "required-archetypes" ||
    (terms.id === "hunt" && terms.requiredArchetypes.length > 0), {
    message: "Required-archetype kill policy needs explicit hunt targets",
  })
  .refine((terms) => terms.cargoKind !== null || terms.cargoGoal === 0, {
    message: "An order without cargo cannot require cargo",
  })
  .refine((terms) => (terms.id === "escort") === (terms.escort !== null), {
    message: "Escort terms belong exclusively to escort orders",
  })
  .refine(
    (terms) =>
      terms.escort === null ||
      terms.templateId === `escort-${terms.escort.routeId}`,
    {
      message: "Escort template must preserve its route identity",
    },
  ) satisfies z.ZodType<ContractTerms>;

export const contractOfferSchema = z
  .object({
    orderId: orderIdSchema,
    contractId: z.enum(SELECTABLE_CONTRACT_IDS),
    templateId: z.enum(CONTRACT_TEMPLATE_IDS),
    standard: contractTermsSchema,
    advanced: contractTermsSchema,
  })
  .strict()
  .refine(
    (offer) =>
      [offer.standard, offer.advanced].every(
        (terms) =>
          terms.id === offer.contractId &&
          terms.templateId === offer.templateId,
      ),
    { message: "Offer variants must share one template and family" },
  )
  .refine(
    (offer) =>
      (offer.standard.escort === null && offer.advanced.escort === null) ||
      (offer.standard.escort !== null &&
        offer.advanced.escort !== null &&
        escortPhysicalKey(offer.standard.escort) ===
          escortPhysicalKey(offer.advanced.escort)),
    { message: "Escort difficulties must share one physical convoy route" },
  ) satisfies z.ZodType<ContractOffer>;

export const contractBoardSchema = z
  .object({
    revision: z.number().int().nonnegative(),
    offers: z
      .array(contractOfferSchema)
      .length(6)
      .refine(
        (offers) =>
          new Set(
            offers.map(
              (offer) =>
                `${offer.contractId}:${offer.standard.escort?.routeId ?? ""}`,
            ),
          ).size === 6 &&
          new Set(offers.map((offer) => offer.orderId)).size === 6,
        {
          message:
            "A board must contain six distinct orders and family/route pairs",
        },
      ),
  })
  .strict();
